import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { evidenceAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import Navbar from '../components/Navbar';
import {
  FileText,
  Image,
  Award,
  FileCode,
  GraduationCap,
  FileSignature,
  Settings,
  File,
  Upload,
  Search,
  Filter,
  Trash2,
  Download,
  Eye,
  Link as LinkIcon,
  Calendar,
  Clock,
  AlertTriangle,
  FolderOpen,
  Plus,
  X,
  ChevronDown,
  Loader2,
  Tag
} from 'lucide-react';

const CATEGORY_ICONS = {
  policy: FileText,
  screenshot: Image,
  certificate: Award,
  audit_log: FileCode,
  training_record: GraduationCap,
  contract: FileSignature,
  configuration: Settings,
  other: File
};

const CATEGORY_COLORS = {
  policy: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  screenshot: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  certificate: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  audit_log: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  training_record: 'bg-green-500/20 text-green-400 border-green-500/30',
  contract: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  configuration: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  other: 'bg-violet-500/20 text-violet-400 border-violet-500/30'
};

export default function EvidenceLibrary() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [evidence, setEvidence] = useState([]);
  const [categories, setCategories] = useState({});
  const [byCategory, setByCategory] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    category: 'other',
    expirationDate: '',
    tags: ''
  });

  useEffect(() => {
    loadData();
  }, [searchTerm, selectedCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, libraryRes] = await Promise.all([
        evidenceAPI.getCategories(),
        evidenceAPI.getLibrary({ search: searchTerm, category: selectedCategory })
      ]);

      setCategories(categoriesRes.data);
      setEvidence(libraryRes.data.evidence || []);
      setByCategory(libraryRes.data.byCategory || {});
    } catch (error) {
      showNotification('Failed to load evidence library', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({
        ...uploadForm,
        file,
        title: uploadForm.title || file.name.replace(/\.[^/.]+$/, '')
      });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      showNotification('Please select a file', 'error');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('expirationDate', uploadForm.expirationDate);
      formData.append('tags', uploadForm.tags);

      await evidenceAPI.upload(formData);
      showNotification('Evidence uploaded successfully', 'success');
      setShowUploadModal(false);
      setUploadForm({
        file: null,
        title: '',
        description: '',
        category: 'other',
        expirationDate: '',
        tags: ''
      });
      loadData();
    } catch (error) {
      showNotification('Failed to upload evidence', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to archive this evidence?')) return;

    try {
      await evidenceAPI.delete(id);
      showNotification('Evidence archived', 'success');
      loadData();
    } catch (error) {
      showNotification('Failed to delete evidence', 'error');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    const now = new Date();
    const daysUntil = (expDate - now) / (1000 * 60 * 60 * 24);
    return daysUntil > 0 && daysUntil <= 30;
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  const totalEvidence = evidence.length;
  const expiringSoonCount = evidence.filter(e => isExpiringSoon(e.expirationDate)).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950/30 to-slate-900">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <FolderOpen className="w-8 h-8 text-violet-400" />
              Evidence Library
            </h1>
            <p className="text-slate-400 mt-1">
              Manage compliance evidence documents across all assessments
            </p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
            data-testid="upload-evidence-btn"
          >
            <Upload className="w-4 h-4" />
            Upload Evidence
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/20 rounded-lg">
                <FolderOpen className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{totalEvidence}</p>
                <p className="text-sm text-slate-400">Total Evidence</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{Object.keys(byCategory).length}</p>
                <p className="text-sm text-slate-400">Categories Used</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <LinkIcon className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {evidence.reduce((sum, e) => sum + (e.usageCount || 0), 0)}
                </p>
                <p className="text-sm text-slate-400">Question Links</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 ${expiringSoonCount > 0 ? 'bg-amber-500/20' : 'bg-slate-500/20'} rounded-lg`}>
                <AlertTriangle className={`w-5 h-5 ${expiringSoonCount > 0 ? 'text-amber-400' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{expiringSoonCount}</p>
                <p className="text-sm text-slate-400">Expiring Soon</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search evidence..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
              data-testid="search-evidence"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-10 pr-8 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-violet-500"
              data-testid="filter-category"
            >
              <option value="">All Categories</option>
              {Object.entries(categories).map(([key, cat]) => (
                <option key={key} value={key}>{cat.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Evidence Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
          </div>
        ) : evidence.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-300 mb-2">No evidence found</h3>
            <p className="text-slate-400 mb-4">Upload your first compliance evidence document</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              Upload Evidence
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {evidence.map((item) => {
              const IconComponent = CATEGORY_ICONS[item.category] || File;
              const colorClass = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other;
              const expired = isExpired(item.expirationDate);
              const expiringSoon = isExpiringSoon(item.expirationDate);

              return (
                <div
                  key={item.id}
                  className={`bg-slate-800/50 border rounded-xl p-4 hover:border-violet-500/50 transition-colors ${
                    expired ? 'border-red-500/50' : expiringSoon ? 'border-amber-500/50' : 'border-slate-700/50'
                  }`}
                  data-testid={`evidence-item-${item.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`p-3 rounded-lg border ${colorClass}`}>
                      <IconComponent className="w-6 h-6" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-white truncate">{item.title}</h3>
                          <p className="text-sm text-slate-400 truncate">{item.fileName}</p>
                        </div>
                        {(expired || expiringSoon) && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            expired ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {expired ? 'Expired' : 'Expiring Soon'}
                          </span>
                        )}
                      </div>

                      {item.description && (
                        <p className="text-sm text-slate-400 mt-1 line-clamp-2">{item.description}</p>
                      )}

                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <File className="w-3 h-3" />
                          {formatFileSize(item.fileSize)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(item.createdAt)}
                        </span>
                        {item.expirationDate && (
                          <span className={`flex items-center gap-1 ${expired ? 'text-red-400' : expiringSoon ? 'text-amber-400' : ''}`}>
                            <Calendar className="w-3 h-3" />
                            Expires: {formatDate(item.expirationDate)}
                          </span>
                        )}
                        {item.usageCount > 0 && (
                          <span className="flex items-center gap-1 text-green-400">
                            <LinkIcon className="w-3 h-3" />
                            Linked to {item.usageCount} question{item.usageCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {/* Tags */}
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {item.tags.map((tag, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-slate-700/50 text-slate-300 rounded text-xs flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <a
                        href={evidenceAPI.getDownloadUrl(item.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-400 hover:text-violet-400 hover:bg-slate-700/50 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-5 h-5" />
                      </a>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700/50 rounded-lg transition-colors"
                        title="Archive"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Upload Evidence</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpload} className="p-6 space-y-4">
              {/* File Input */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">File *</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center cursor-pointer hover:border-violet-500 transition-colors"
                >
                  {uploadForm.file ? (
                    <div className="flex items-center justify-center gap-2 text-violet-400">
                      <File className="w-5 h-5" />
                      <span>{uploadForm.file.name}</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-400">Click to select a file</p>
                      <p className="text-xs text-slate-500 mt-1">PDF, DOC, PNG, JPG, CSV, etc.</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Title *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  required
                  placeholder="e.g., Information Security Policy v2.0"
                  className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
                  data-testid="evidence-title"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  data-testid="evidence-category"
                >
                  {Object.entries(categories).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.name} - {cat.description}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Add details about this evidence..."
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500 resize-none"
                  data-testid="evidence-description"
                />
              </div>

              {/* Expiration Date */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Expiration Date (optional)</label>
                <input
                  type="date"
                  value={uploadForm.expirationDate}
                  onChange={(e) => setUploadForm({ ...uploadForm, expirationDate: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  data-testid="evidence-expiration"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                  placeholder="e.g., HIPAA, encryption, annual"
                  className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
                  data-testid="evidence-tags"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !uploadForm.file}
                  className="flex-1 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="submit-upload"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
